import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ResourceSankey } from "../Components/Resources/ResourceSankey";
import { Loading } from "../Components/Loading/Loading";
import { redirectToLogin } from "../utils/auth";

function shortName(email) {
  if (!email) return "Unknown";
  return email.split("@")[0].split(".").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export const Resources = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activePreset, setActivePreset] = useState("all");
  const [selectedProject, setSelectedProject] = useState("");
  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    if (!authToken) {
      redirectToLogin()
      return;
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/API/V2/analytics/resources`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => {
        if (res.status === 401) {
          redirectToLogin()
          return;
        }
        return res.json();
      })
      .then((data) => {
        if (!data || data.error) return;
        setTickets(data.tickets || []);
        setUsers(data.users || []);
        setTeams(data.teams || []);
        setLoading(false);
      })
      .catch((err) => console.error("Fetch error:", err));
  }, [authToken]);

  const handlePreset = (key) => {
    setActivePreset(key);
    if (key === "all") {
      setStartDate("");
      setEndDate("");
      return;
    }
    const days = { "7d": 7, "30d": 30, "90d": 90 }[key];
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().slice(0, 10));
    setEndDate(end.toISOString().slice(0, 10));
  };

  const filteredTickets = useMemo(() => {
    if (!startDate && !endDate) return tickets;
    return tickets.filter((t) => {
      const d = new Date(t.createdAt);
      if (isNaN(d.getTime())) return false;
      if (startDate && d < new Date(startDate)) return false;
      if (endDate) {
        const e = new Date(endDate);
        e.setHours(23, 59, 59, 999);
        if (d > e) return false;
      }
      return true;
    });
  }, [tickets, startDate, endDate]);

  const projects = useMemo(() => {
    const orgs = new Set(filteredTickets.map((t) => t.org).filter(Boolean));
    return [...orgs].sort();
  }, [filteredTickets]);

  const userAccessMap = useMemo(() => {
    const map = {};
    for (const u of users) map[u.email] = 1;
    return map;
  }, [users]);

  const userTeamMap = useMemo(() => {
    const teamLookup = {};
    for (const t of teams) teamLookup[t.value] = t.key;
    const map = {};
    for (const u of users) {
      map[u.email] = {
        name: u.name || shortName(u.email),
        teamName: teamLookup[u.team] || "Unknown Team",
      };
    }
    return map;
  }, [users, teams]);

  const projectTickets = useMemo(() => {
    if (!selectedProject) return filteredTickets;
    return filteredTickets.filter((t) => t.org === selectedProject);
  }, [filteredTickets, selectedProject]);

  const handleEngineerClick = (displayName) => {
    const user = users.find((u) => shortName(u.email) === displayName);
    if (user) navigate(`/Resource/${user.email.split("@")[0]}`);
  };

  if (loading) return <Loading />;

  const presets = [
    { key: "all", label: "All" },
    { key: "7d", label: "7d" },
    { key: "30d", label: "30d" },
    { key: "90d", label: "90d" },
  ];

  return (
    <div className="p-2 rounded-3xl mx-6 mt-4 flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="mr-auto text-2xl font-black tracking-tight leading-snug">Resources</h1>
        <div className="flex items-center gap-4 flex-wrap">
          <select
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {presets.map((p) => (
              <button
                key={p.key}
                className={`px-4 py-1.5 text-sm font-bold transition ${
                  activePreset === p.key
                    ? "bg-jiojbmpurple text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                onClick={() => handlePreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setActivePreset("");
              }}
            />
            <span className="text-gray-400">&ndash;</span>
            <input
              type="date"
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setActivePreset("");
              }}
            />
          </div>
        </div>
      </div>

      <ResourceSankey
        applicationName={selectedProject || "All Projects"}
        tickets={projectTickets}
        userAccessMap={userAccessMap}
        userTeamMap={userTeamMap}
        height={360}
        onEngineerClick={handleEngineerClick}
      />
    </div>
  );
};
